/* eslint-disable no-template-curly-in-string */
import { Button, Card, CardContent, Input, Label, NativeSelect } from "@altimateai/lego";
import { InputParameter } from "./constants";
import {
  UseFormRegister,
  UseFormWatch,
  UseFormSetValue,
  FieldValues,
  FormState,
} from "react-hook-form";
import ApiHeaders from "./ApiHeaders";
import ToolField from "./ToolField"; // Keep this import
import { useEffect } from "react";
import { Editor } from "@monaco-editor/react";
import { AutoPlusIcon, DeleteIcon } from "@assets/icons";
import { Stack } from "@altimateai/lego";

const CustomField = ({
  config,
  register,
  formState,
  watch,
  setValue,
  fieldPrefix = "field-",
  registerPrefix = "",
}: {
  config: InputParameter;
  register: UseFormRegister<FieldValues>;
  formState: FormState<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  fieldPrefix?: string;
  registerPrefix?: string;
}) => {
  const fieldId = `${fieldPrefix}${config.key}`;

  const watchedValue = watch(`${registerPrefix}${config.key}`);
  const fallbackValue = config.defaultValue;

  useEffect(() => {
    if (watchedValue === undefined && fallbackValue !== undefined) {
      setValue(`${registerPrefix}${config.key}`, fallbackValue);
    }
  }, [watchedValue, fallbackValue, setValue, registerPrefix, config.key]);

  switch (config.type) {
    case "headers":
      return (
        <ApiHeaders
          headers={watch(`${registerPrefix}${config.key}`)}
          setHeaders={updater => {
            // updater can be a function or a value
            setValue(
              `${registerPrefix}${config.key}`,
              typeof updater === "function"
                ? updater(watch(`${registerPrefix}${config.key}`) || [])
                : updater
            );
          }}
        />
      );
    case "select":
      return (
        <div>
          <Label>{config.name}</Label>
          <NativeSelect
            size="sm"
            variant="ghost"
            options={
              config.options?.map(option => ({
                label: option,
                value: option,
              })) || []
            }
            id={fieldId}
            value={watch(`${registerPrefix}${config.key ?? ""}`)}
            onChange={(value: string) => setValue(`${registerPrefix}${config.key ?? ""}`, value)}
          />
          {formState.errors?.[config.key ?? ""] && (
            <p className="al-text-sm al-text-destructive">
              {formState.errors[config.key ?? ""]?.message?.toString()}
            </p>
          )}
        </div>
      );
    case "multi": {
      const items = (watch(`${registerPrefix}${config.key}`) as string[]) || [];

      const handleAddItem = () => {
        setValue(`${registerPrefix}${config.key}`, [...items, ""]);
      };

      const handleRemoveItem = (index: number) => {
        const updatedItems = items.filter((_, i) => i !== index);
        setValue(`${registerPrefix}${config.key}`, updatedItems);
      };

      const handleItemChange = (index: number, value: string) => {
        const updatedItems = [...items];
        updatedItems[index] = value;
        setValue(`${registerPrefix}${config.key}`, updatedItems);
      };

      return (
        <div>
          <Label>{config.name}</Label>
          <Stack direction="column">
            <Stack direction="column">
              {items.map((item, index) => (
                <Stack key={index}>
                  <Input
                    type="text"
                    value={item}
                    onChange={e => handleItemChange(index, e.target.value)}
                    placeholder={`Item ${index + 1}`}
                    className="al-flex-1"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveItem(index)}
                  >
                    <DeleteIcon className="al-w-4 al-h-4 al-text-destructive" />
                  </Button>
                </Stack>
              ))}
            </Stack>
            <Button type="button" variant="outline" size="sm" onClick={handleAddItem}>
              <AutoPlusIcon className="al-w-4 al-h-4 al-mr-2" />
              Add {config.name}
            </Button>
          </Stack>
          {formState.errors?.[config.key ?? ""] && (
            <p className="al-text-sm al-text-destructive">
              {formState.errors[config.key ?? ""]?.message?.toString()}
            </p>
          )}
        </div>
      );
    }
    case "environmentVariables": {
      const envVariables =
        (watch(`${registerPrefix}${config.key}`) as { key: string; value: string }[]) || [];

      const handleAddEnvVariable = () => {
        setValue(`${registerPrefix}${config.key}`, [...envVariables, { key: "", value: "" }]);
      };

      const handleRemoveEnvVariable = (index: number) => {
        const updatedEnvVariables = envVariables.filter((_, i) => i !== index);
        setValue(`${registerPrefix}${config.key}`, updatedEnvVariables);
      };

      const handleEnvVariableChange = (index: number, field: "key" | "value", value: string) => {
        const updatedEnvVariables = [...envVariables];
        updatedEnvVariables[index][field] = value;
        setValue(`${registerPrefix}${config.key}`, updatedEnvVariables);
      };

      return (
        <Card>
          <CardContent className="al-pt-4">
            <div>
              <Label>{config.name}</Label>
              <p>{config.description}</p>
              <Stack direction="column">
                <Stack direction="column">
                  {envVariables.map((envVar, index) => (
                    <Stack key={index} direction="row" className="al-items-center">
                      <Input
                        type="text"
                        value={envVar.key}
                        onChange={e => handleEnvVariableChange(index, "key", e.target.value)}
                        placeholder={`Key ${index + 1}`}
                        className="al-flex-1"
                      />
                      <Input
                        type="text"
                        value={envVar.value}
                        onChange={e => handleEnvVariableChange(index, "value", e.target.value)}
                        placeholder={`Value ${index + 1}`}
                        className="al-flex-1 al-ml-2"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveEnvVariable(index)}
                        className="al-ml-2"
                      >
                        <DeleteIcon className="al-w-4 al-h-4 al-text-destructive" />
                      </Button>
                    </Stack>
                  ))}
                </Stack>
                <p className="al-text-sm al-text-gray-500">
                  You can refer values from connections in environment variables. For example, if
                  you have a connection variable named as <code>API_TOKEN</code>, you can refer to
                  it as <code>{"${API_TOKEN}"}</code> in environment variables.
                </p>
                <Button type="button" variant="outline" size="sm" onClick={handleAddEnvVariable}>
                  <AutoPlusIcon className="al-w-4 al-h-4 al-mr-2" />
                  Add Environment Variable
                </Button>
              </Stack>
              {formState.errors?.[config.key ?? ""] && (
                <p className="al-text-sm al-text-destructive">
                  {formState.errors[config.key ?? ""]?.message?.toString()}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      );
    }
    case "tools":
      return (
        <ToolField
          configKey={config.key ?? ""}
          configLabel={config.name}
          register={register}
          formState={formState}
          watch={watch}
          setValue={setValue}
          registerPrefix={registerPrefix}
        />
      );
    case "code":
      return (
        <div>
          <Label>{config.name}</Label>
          <div className="al-border al-p-1 al-rounded">
            <Editor
              height="300px"
              defaultLanguage={config.key === "code" ? "python" : "yaml"}
              value={watch(`${registerPrefix}${config.key}`) ?? ""}
              onChange={val => setValue(`${registerPrefix}${config.key}`, val ?? "")}
              options={{
                fontSize: 11,
                minimap: { enabled: false },
                wordWrap: "on",
                lineNumbers: "off",
              }}
            />
          </div>
          {formState.errors?.[config.key ?? ""] && (
            <p className="al-text-sm al-text-destructive">
              {formState.errors[config.key ?? ""]?.message?.toString()}
            </p>
          )}
        </div>
      );
    default:
      return (
        <div>
          <Label>{config.name}</Label>
          <Input
            id={fieldId}
            type={config.type}
            {...register(`${registerPrefix}${config.key ?? ""}`)}
            placeholder={config.description}
          />
          <p>{config.description}</p>
          {formState.errors?.[config.key ?? ""] && (
            <p className="al-text-sm al-text-destructive">
              {formState.errors[config.key ?? ""]?.message?.toString()}
            </p>
          )}
        </div>
      );
  }
};

export default CustomField;
